import { useWindowVirtualizer } from "@tanstack/react-virtual";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Col, Form, Stack } from "react-bootstrap";
import DataDescription from "../components/DataDescription";
import EmptyRow from "../components/EmptyRow";
import ExportData from "../components/ExportData";
import { useFetchData } from "../components/HelperHooks";
import PageDesciption from "../components/PageDesciption";
import AsyncSongPicker from "../components/Pickers/AsyncSongPicker";
import StationPicker from "../components/Pickers/StationPicker";
import SearchInput from "../components/SearchInput";
import { useUserFormState } from "../components/UserSelection";
import { DataContent, InputSelection, ModulePage, SoleDescriptionRow, VirtualizedWindowContainer } from "../components/pageSections";
import { useSongCatalogueModal } from "../components/songCatalogue/SongCatalogueModal";
import SongSearchHeader from "../components/songSearch/SongSearchHeader";
import SongSearchRow from "../components/songSearch/SongSearchRow";
import useSortableData from "../components/useSortableData";

const extraDefaults = { artist: "", title: "" };

export default function SongSearch() {
    const moduleName = "songSearch";

    const [formData, setFormData] = useUserFormState(moduleName, extraDefaults);
    const [loading, errorCode, data, fetchData] = useFetchData();

    const [submittedFormData, setSubmittedFormData] = useState(null);
    const [filterData, setFilterData] = useState({ searchString: "" });
    const [showItemId, setShowItemId] = useState(false);
    const [resultCount, setResultCount] = useState(0);

    const { setCatalogueSong, SongCatalogueModalComponent } = useSongCatalogueModal();

    const tableContainerRef = useRef(null);

    async function handleFormSubmit(event) {
        event.preventDefault();
        if ((formData.artist + formData.title).length < 3) return;
        const url = `/items/songs/?artist=${encodeURIComponent(formData.artist)}&title=${encodeURIComponent(formData.title)}&radio_id=${
            formData.station[0]?.radio_id ?? 0
        }`;
        await fetchData(url);
        setSubmittedFormData({ ...formData });
    }

    const songsMemo = useMemo(() => {
        if (data) {
            let filteredData = data;
            setResultCount(filteredData.length);
            if (filterData.searchString) {
                filteredData = filteredData.filter(
                    x =>
                        x.artist.toLowerCase().includes(filterData.searchString.toLowerCase().trim()) ||
                        x.title.toLowerCase().includes(filterData.searchString.toLowerCase().trim()) ||
                        x.release_year?.toString().includes(filterData.searchString.trim()) ||
                        x.spins?.toString().includes(filterData.searchString.trim())
                );
            }
            return filteredData;
        }
    }, [data, filterData.searchString]);

    const { sortedItems, requestSort, sortConfig } = useSortableData(songsMemo);

    useMemo(() => {
        if (sortedItems) {
            setResultCount(sortedItems.length);
        }
    }, [sortedItems]);

    const csvHeaders = ["Artist", "Title", "Release Year", "Total Spins", "Item ID"];
    const csvData = useMemo(() => sortedItems?.map(x => [x.artist, x.title, x.release_year, x.spins, x.item_id]), [sortedItems]);

    const virtualizer = useWindowVirtualizer({
        count: sortedItems?.length || 1,
        estimateSize: useCallback(() => 40, []),
        getItemKey: useCallback(i => sortedItems?.length && sortedItems[i].item_id, [sortedItems]),
        overscan: 30,
        scrollMargin: tableContainerRef.current?.offsetTop ?? 0,
    });

    return (
        <ModulePage>
            <ModulePage.Title>Song Search</ModulePage.Title>
            <ModulePage.InputSelection handleFormSubmit={handleFormSubmit} submitText="Search" disabled={formData.artist.length + formData.title.length < 3}>
                <InputSelection.Main>
                    <AsyncSongPicker formData={formData} setFormData={setFormData} name={moduleName} formKey={"artist"} label={"Artist"} />
                    <AsyncSongPicker formData={formData} setFormData={setFormData} name={moduleName} formKey={"title"} label={"Title"} />
                    <StationPicker formData={formData} setFormData={setFormData} name={moduleName} includeCompetitors label="Only search on" />
                </InputSelection.Main>
            </ModulePage.InputSelection>
            <ModulePage.DataContent loading={loading} errorCode={errorCode}>
                <DataContent.Placeholder show={!data}>
                    <PageDesciption
                        title={"Song Search"}
                        whatList={[
                            "Search for songs by artist, title, or even just a word",
                            "Search your entire portfolio or only on a specific station",
                            "You can check the song score on your own station(s) with the trend button",
                        ]}
                        knowList={[
                            {
                                text: "Just start typing and the system will suggest songs based on what you write, even just one word! (try typing “love” in the title bar for your valentines playlists..)",
                            },
                            { text: "A great way to find less played songs by popular artists, just sort by plays and ignore the top of the list" },
                            { text: "Songs are sorted by spins – click on any column header to change the sorting" },
                        ]}
                        relatedLinks={[
                            {
                                title: "Your Song",
                                url: "https://radioanalyzer.com/blog/your-song",
                            },
                        ]}
                        videoSrc="https://www.youtube.com/embed/8Ukv6ZiDqlQ?si=VlgqKUzFY7RtYiaC"
                    />
                </DataContent.Placeholder>
                {data && (
                    <>
                        <SoleDescriptionRow>
                            <DataDescription>
                                {submittedFormData.station[0]?.radio_id && <DataDescription.Station radioId={submittedFormData.station[0].radio_id} />}
                                {submittedFormData.artist && <DataDescription.Simple label="Searched Artist" value={submittedFormData.artist} />}
                                {submittedFormData.title && <DataDescription.Simple label="Searched Title" value={submittedFormData.title} />}
                                <DataDescription.Simple label={"Results"} value={resultCount} />
                            </DataDescription>
                        </SoleDescriptionRow>
                        <Stack direction="horizontal" gap={3} className=" mb-2 flex-wrap-reverse row-gap-1">
                            <Col xs={12} sm xl={5}>
                                <SearchInput filterData={filterData} setFilterData={setFilterData} />
                            </Col>
                            <Stack direction="horizontal" gap={1} className=" ms-sm-auto">
                                <span className="me-1">Item ID:</span>
                                <Form.Switch
                                    className="form-switch-lg"
                                    id="song-search-item-id"
                                    size="m"
                                    checked={showItemId}
                                    onChange={() => setShowItemId(!showItemId)}
                                />
                            </Stack>
                            <Col xs={"auto"} className="ms-auto ms-sm-0">
                                <Stack direction="horizontal" gap={1}>
                                    <span className="me-1 d-none d-md-inline-block">Download:</span>
                                    <ExportData data={csvData} headers={csvHeaders} filename={`Song Search`} />
                                </Stack>
                            </Col>
                        </Stack>
                        <SongSearchHeader requestSort={requestSort} sortConfig={sortConfig} showItemId={showItemId} />
                        <VirtualizedWindowContainer containerRef={tableContainerRef} virtualizer={virtualizer}>
                            {virtualizer.getVirtualItems().map(virtualRow => {
                                const item = sortedItems[virtualRow.index];
                                if (!item) return <EmptyRow key={virtualRow.key} text="No songs" />;
                                return (
                                    <SongSearchRow
                                        item={item}
                                        key={virtualRow.key}
                                        virtualRow={virtualRow}
                                        virtualizer={virtualizer}
                                        showItemId={showItemId}
                                        setCatalogueSong={setCatalogueSong}
                                    />
                                );
                            })}
                        </VirtualizedWindowContainer>
                    </>
                )}
                <SongCatalogueModalComponent />
            </ModulePage.DataContent>
        </ModulePage>
    );
}
